<script setup lang="ts">
import { useOnline, useSessionStorage } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GenericEvents } from '~/enums/tracking/tracking-events.enums';
import { AuthViaStorageKey } from '~/enums/auth.enum';

const isOnline = useOnline();
const { dispatchMessage } = useSystemMessages();
const { checkAndOpenEducatorPrompt } = useEducatorPrompt();
const { checkAndOpenInvitationDialog, isInvitationDialogOpen, autoOpenInvitation } = useB2BInvitation();
const { startCountdown, countdown } = useCountdown();
const { user } = useUserStore();

const updateNoticeVisible = ref(false);

const showB2BInvitationSuccessDialog = ref(false);

const b2bAccepted = useSessionStorage(AuthViaStorageKey.B2BAccepted, null, { listenToStorageChanges: false });

watch(isOnline, (newValue: boolean) => {
  if (newValue) {
    dispatchMessage('Back online.', 'success', 'Your browser is back online.');
  } else {
    dispatchMessage('You are offline.', 'warn', 'Your browser went offline, please check your internet connection.', 15000);
  }
});

useNuxtApp().hook('app:manifest:update', () => {
  if (updateNoticeVisible.value) {
    return;
  }

  usePVToastService().add({
    severity: 'info',
    summary: 'App Update',
    group: 'appUpdate',
    closable: false,
  });

  updateNoticeVisible.value = true;
});

const route = useRoute();

const checkB2BAcceptedWelcome = () => {
  if (!b2bAccepted.value) {
    return;
  }

  if (route.path.includes('/dashboard') || route.path.includes('/account') || route.path.includes('/courses')) {
    showB2BInvitationSuccessDialog.value = true;
    startCountdown(5, () => {
      showB2BInvitationSuccessDialog.value = false;
    });
    b2bAccepted.value = null;
    useSessionStorage(AuthViaStorageKey.B2BAccepted, null, { listenToStorageChanges: false }).value = null;
  }
};

useNuxtApp().hook('page:finish', () => {
  checkAndOpenEducatorPrompt();
  checkAndOpenInvitationDialog();
  checkB2BAcceptedWelcome();
});

const reloadPage = () => {
  useTracking().dispatchTrackingEvent(GenericEvents.AppUpdateButtonClicked);
  window.document.location.reload();
};

const handleSuccess = () => {
  showB2BInvitationSuccessDialog.value = true;
  isInvitationDialogOpen.value = false;
  startCountdown(5, () => {
    showB2BInvitationSuccessDialog.value = false;
  });
};

useHead({
  titleTemplate: titleChunk => {
    return titleChunk ? `${titleChunk} | Medmastery` : 'Medmastery';
  },
});

</script>

<template>
  <NuxtLayout>
    <SystemMessages />
    <DynamicDialog />
    <ConfirmDialog />

    <DashboardB2BInvitationDialog
      v-if="autoOpenInvitation"
      v-model:visible="isInvitationDialogOpen"
      :notification-id="autoOpenInvitation.id"
      :group-name="autoOpenInvitation?.data?.groupName as string"
      :inviter-name="autoOpenInvitation?.data?.inviterName as string"
      :invitation-link="autoOpenInvitation?.data?.invitationLink as string"
      @accepted="handleSuccess"
    />
    <DashboardB2BInvitationSuccessDialog
      v-model:visible="showB2BInvitationSuccessDialog"
      :group-name="(autoOpenInvitation?.data?.groupName || user?.group?.groupName) as string"
      :countdown="countdown"
    />

    <NuxtPage />

    <Toast
      group="appUpdate"
      position="bottom-left"
    >
      <template #container>
        <div class="p-4 flex gap-4">
          <FontAwesomeIcon
            class="text-24 mt-[calc((36px-24px)/2)]"
            :icon="['far', 'rocket-launch']"
          />

          <div class="flex flex-col gap-2">
            <div>
              <b>We've made some updates!</b> Refresh now for the best experience.
            </div>

            <div>
              <Button
                size="small"
                class="bg-neutral-80 border-0"
                @click="reloadPage"
              >
                <FontAwesomeIcon
                  class="mr-2"
                  icon="far fa-rotate"
                />
                <span class="font-bold">Update</span>
              </Button>
            </div>
          </div>
        </div>
      </template>
    </Toast>
  </NuxtLayout>
</template>
